import { useNavigate, useParams, NavigateOptions } from "react-router-dom";

import { buildUrlWithOrg } from "@/shared/lib/brandUtils";

/**
 * Hook personnalisé pour gérer la navigation avec organizationId
 * Fournit des méthodes de navigation qui préservent l'organizationId dans l'URL
 * Réagit automatiquement aux changements de route
 */
export const useNavigation = () => {
  const navigate = useNavigate();
  const { idorga } = useParams<{ idorga: string }>();
  const organizationId = idorga;

  /**
   * Navigue vers une route en préservant l'organizationId
   * @param path - Le chemin vers lequel naviguer
   * @param options - Options de navigation (ex: { replace: true })
   */
  const navigateWithOrg = (path: string, options?: NavigateOptions) => {
    const fullPath = buildUrlWithOrg(path, organizationId);
    navigate(fullPath, options);
  };

  /**
   * Navigue vers la page d'accueil de l'organisation
   */
  const navigateToHome = () => {
    navigateWithOrg("/");
  };

  /**
   * Parcours commande - page d'accueil (formulaire)
   */
  const navigateToOrder = () => {
    navigateWithOrg("/order");
  };

  /**
   * Parcours commande - paiement
   */
  const navigateToPayment = () => {
    navigateWithOrg("/order/payment");
  };

  /**
   * Parcours commande - confirmation
   * IMPORTANT: Utilise replace: true par défaut pour empêcher l'utilisateur de revenir
   * en arrière sur la page de paiement via le bouton du navigateur
   * @param options - Options de navigation (ex: { replace: false } pour ne pas remplacer l'historique)
   */
  const navigateToConfirmation = (options?: NavigateOptions) => {
    navigateWithOrg("/order/confirmation", { replace: true, ...options });
  };

  /**
   * Navigue vers les commandes du client (route globale regroupant toutes les orgas)
   */
  const navigateToCustomerOrders = () => {
    navigate("/my-orders");
  };

  /**
   * Navigue vers l'administration
   */
  const navigateToAdmin = () => {
    navigateWithOrg("/admin");
  };

  /**
   * Admin - Détail d'une commande
   */
  const navigateToOrderDetail = (orderId: string) => {
    navigateWithOrg(`/admin/orders/${orderId}`);
  };

  /**
   * Navigue vers la configuration de l'organisation
   */
  const navigateToConfiguration = () => {
    navigateWithOrg("/admin/configuration");
  };

  return {
    navigateWithOrg,
    navigateToHome,
    navigateToOrder,
    navigateToPayment,
    navigateToConfirmation,
    navigateToCustomerOrders,
    navigateToAdmin,
    navigateToOrderDetail,
    navigateToConfiguration,
    organizationId,
  };
};
